import { useEffect, useState, useCallback, useContext } from 'react';
import { Flex } from 'antd';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import { MainForm } from '@Components/index';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import {
  useGetAttendanceTimeMutation,
  useGetResumptionLeavesMutation,
  useSaveDraftResumptionRequestMutation,
} from '@Features/EmployeeSelfService/requests/ResumptionRequests/store/api/resumptionRequestApiSlice';
import { resumptionRequestActions } from '@Features/EmployeeSelfService/requests/ResumptionRequests/store/resumptionRequestSlice';
import { resumptionRequestCreateRequisitionInfo } from '@Features/EmployeeSelfService/requests/ResumptionRequests/store/selectors/resumptionRequestSelectors';
import {
  useAppDispatch,
  useAppSelector,
  useNavigationToListRequests,
} from '@Hooks/index';
import { useSubmitHrmRequestMutation } from '@Store/api/basicHrmApiSlice';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import { validateForm } from '@Features/utils/validateForm';
import { NotificationContext } from '@Context/NotificationContext';
import { FooterActions } from '@Enum/index';
import { OptionNum } from '@Components/types/options';
import { getResumptionWorkShiftDict } from '@Store/selectors/dictionarySelector';
import { transformDictToOptionsNum } from '@Utils/transformToOptions';
import { RequestError, ResumptionRequestFormRequest } from '@Types/api/form';
import { ResumptionLeavesResponse } from '@Types/api/form/resumptionRequest/ResumpationLeaves';
import { initialFormValues } from '../initialValues';

import type { ResumptionRequestFormValues } from '../../types';
import { RequestDetailsSection } from '../components';
import { getValidationSchema } from './validationSchema';
import { ATTENDANCE_TIME_ERROR } from '../constants';

const descriptionAlert = (
  <div>
    Note:
    <ol type="1">
      <li>
        Date difference between leave end date and resuming date will be
        considered as leave days.(Will be adjusted automatically in your Annual
        / un-paid leave).
      </li>
      <li>
        Request will not be approved until you submit the Passport to PRO
        Department.
      </li>
    </ol>
    Note:
    <ol type="1">
      <li>
        Please enter all the details and submit the data. After submission
        Resumption request will go to higher officials approval and you can
        cancel it at any point of time before it gets approved by HR & Payroll
        departments. Each request will be monitored by HR & Payroll department.
      </li>
    </ol>
  </div>
);

const ResumptionRequestCreate: React.FC = () => {
  const {
    saveSuccessNotification,
    errorNotification,
    submitSuccessNotification,
  } = useContext(NotificationContext);
  const dispatch = useAppDispatch();
  const { handleBackToListRequests } = useNavigationToListRequests();
  const currentUser = useAppSelector(currentUserSelector);
  const requisitionInfo = useAppSelector(
    resumptionRequestCreateRequisitionInfo
  );
  const resumptionWorkShifts = useAppSelector(getResumptionWorkShiftDict);
  const [getAttendanceTime] = useGetAttendanceTimeMutation();
  const [getResumptionLeaves] = useGetResumptionLeavesMutation();
  const [saveDraftResumptionRequest] = useSaveDraftResumptionRequestMutation();
  const [submitResumptionRequest] = useSubmitHrmRequestMutation();

  const [attendanceTime, setAttendanceTime] = useState('');
  const [disabledSaveForm, setDisableSaveForm] = useState(false);
  const [resumingFromOptions, setResumingFromOptions] = useState<OptionNum[]>(
    []
  );
  const [resumptionLeaves, setResumptionLeaves] =
    useState<ResumptionLeavesResponse>();

  useEffect(
    () => () => {
      dispatch(resumptionRequestActions.setResumptionRequest(null));
    },
    []
  );

  useEffect(() => {
    if (currentUser.empNumber) {
      getResumptionLeaves({
        empNumber: currentUser.empNumber,
      })
        .unwrap()
        .then((res) => {
          setResumptionLeaves(res);
        });
    }
  }, [currentUser.empNumber]);

  useEffect(() => {
    if (resumptionLeaves?.ResDates) {
      const resumptionOptions = resumptionLeaves?.ResDates.map(
        (leave) =>
          ({
            label: leave.LeaveData,
            value: Number(leave.ReqID),
          }) as OptionNum
      );

      setResumingFromOptions(resumptionOptions);
    }
  }, [resumptionLeaves]);

  const handleSubmitForm = useCallback(
    (values: ResumptionRequestFormValues): void => {
      const submitForm = async (): Promise<void> => {
        let body = {
          empNumber: currentUser.empNumber,
          currentLocation: values.currentLocation,
          leaveTypeID: values.resumingFromLeaveRequestId as number,
          startDate: values.resumingOnDate,
          ticketRequired: values.workShift,
          remarks: values.remarks,
          destination: attendanceTime,
          totDays: values.totDays,
        } as ResumptionRequestFormRequest;

        if (requisitionInfo?.requisitionNo) {
          body = {
            ...body,
            requestId: Number(requisitionInfo?.requisitionNo),
          };
        }

        setDisableSaveForm(true);

        try {
          const {
            createdDate,
            reqStatusId,
            requestId: reqId,
          } = await saveDraftResumptionRequest(body).unwrap();

          dispatch(
            resumptionRequestActions.setResumptionRequest({
              createdDate,
              requestId: reqId,
              reqStatusId,
            })
          );

          await submitResumptionRequest(String(reqId)).unwrap();

          submitSuccessNotification(String(reqId));

          setDisableSaveForm(false);
          handleBackToListRequests();
        } catch (error) {
          const message = (error as RequestError)?.data;
          errorNotification(message);

          setDisableSaveForm(false);
        }
      };

      submitForm();
    },
    [currentUser, requisitionInfo, handleBackToListRequests, attendanceTime]
  );

  const schema = getValidationSchema();

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: handleSubmitForm,
  });

  const onResumingFromChange = useCallback(
    (leaveRequestId: number) => {
      const leaveItem = resumptionLeaves?.ResDates?.find(
        (leave) => Number(leave.ReqID) === leaveRequestId
      );

      if (leaveItem) {
        const leaveSplittedDate = leaveItem?.LeaveData.split(':');
        const leaveEndDate = leaveSplittedDate[leaveSplittedDate.length - 1];
        formik.setFieldValue('resumingFromLeaveRequestEndDate', leaveEndDate);
        formik.setFieldValue('totDays', leaveItem?.ReqIDSync);
      }
    },
    [resumptionLeaves, formik]
  );

  useEffect(() => {
    if (currentUser.currentLocation) {
      formik.setFieldValue('currentLocation', currentUser.currentLocation);
    }
  }, [currentUser.currentLocation]);

  useEffect(() => {
    if (formik.values.resumingOnDate) {
      getAttendanceTime(
        dayjs(formik.values.resumingOnDate)
          ?.startOf('day')
          ?.format('YYYY-MM-DD')
      )
        .unwrap()
        .then((resp) => {
          const validValue = resp.intime || resp.outime;
          setAttendanceTime(validValue);
        })
        .catch(() => {
          formik.setFieldError('resumingOnDate', ATTENDANCE_TIME_ERROR);
        });
    }
  }, [formik.values.resumingOnDate, getAttendanceTime]);

  const handleSaveAsDraft = useCallback((): void => {
    const saveAsDraft = async (): Promise<void> => {
      const body = {
        empNumber: currentUser.empNumber,
        currentLocation: formik.values.currentLocation,
        leaveTypeID: formik.values.resumingFromLeaveRequestId as number,
        startDate: formik.values.resumingOnDate,
        ticketRequired: formik.values.workShift,
        remarks: formik.values.remarks,
        destination: attendanceTime,
        totDays: formik.values.totDays,
      };

      setDisableSaveForm(true);

      try {
        const { createdDate, reqStatusId, requestId } =
          await saveDraftResumptionRequest(body).unwrap();

        dispatch(
          resumptionRequestActions.setResumptionRequest({
            createdDate,
            requestId,
            reqStatusId,
          })
        );

        saveSuccessNotification(String(requestId));

        setDisableSaveForm(false);
        handleBackToListRequests();
      } catch (error) {
        const message = (error as RequestError)?.data;
        errorNotification(message);

        setDisableSaveForm(false);
      }
    };

    const saveAsDraftHandler = async (): Promise<void> => {
      const isFormValid = await validateForm(schema, formik);

      if (!isFormValid) {
        return;
      }

      await saveAsDraft();
    };

    saveAsDraftHandler();
  }, [currentUser, formik, handleBackToListRequests, attendanceTime, schema]);

  const handleFooterAction = useCallback(
    (action: FooterActions) => {
      if (action === FooterActions.SAVE_AS_DRAFT) {
        handleSaveAsDraft();
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }
    },
    [handleBackToListRequests, handleSaveAsDraft]
  );

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        handleSubmit={formik.handleSubmit}
        userInfo={currentUser}
        requisitionInfo={requisitionInfo}
        onFooterAction={handleFooterAction}
        descriptionAlert={descriptionAlert}
        disabledSaveForm={disabledSaveForm}
        formType="create"
      >
        <FormItems>
          <RequestDetailsSection
            label="Request Details"
            formik={formik}
            formApproval={false}
            resumingFromOptions={resumingFromOptions}
            attendanceTime={attendanceTime}
            onResumingFromChange={onResumingFromChange}
            resumptionWorkShiftOptions={transformDictToOptionsNum(
              resumptionWorkShifts
            )}
          />
        </FormItems>
      </MainForm>
    </Flex>
  );
};

export default ResumptionRequestCreate;
